// Appeal section — allows suspended users to submit an appeal

import { useState } from 'react'
import { useAuth } from '@/lib/auth-context'

export function AppealSection(): React.JSX.Element | null {
  const { account, refetchAccount } = useAuth()
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!account) return null

  const level = account.suspension_level ?? 0

  // Only show for suspended accounts (level 2 or 3)
  if (level < 2 || level > 3) return null

  // Already has pending appeal
  if (account.appeal_status === 'pending') {
    return (
      <div className="rounded-xl border border-warning/30 bg-warning/5 p-6">
        <h2 className="text-lg font-semibold text-text-primary">Appeal Status</h2>
        <p className="mt-2 text-sm text-text-secondary">
          Your appeal has been submitted and is pending review. We will notify you by email once a
          decision is made.
        </p>
        <div className="mt-3 rounded-lg bg-surface-1 p-3">
          <p className="text-xs text-text-tertiary">
            Submitted{' '}
            {account.appeal_submitted_at
              ? new Date(account.appeal_submitted_at).toLocaleDateString()
              : 'recently'}
          </p>
        </div>
      </div>
    )
  }

  // Appeal denied
  if (account.appeal_status === 'denied') {
    return (
      <div className="rounded-xl border border-danger/30 bg-red-50/50 p-6">
        <h2 className="text-lg font-semibold text-text-primary">Appeal Denied</h2>
        <p className="mt-2 text-sm text-text-secondary">
          Your appeal was reviewed and denied. If you have additional information, contact{' '}
          <a href="mailto:support@one-email.com" className="text-brand-500 hover:text-brand-600">
            support@one-email.com
          </a>
          .
        </p>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="rounded-xl border border-success/30 bg-green-50/50 p-6">
        <h2 className="text-lg font-semibold text-text-primary">Appeal Submitted</h2>
        <p className="mt-2 text-sm text-text-secondary">
          Thank you. Our team will review your appeal and respond by email.
        </p>
      </div>
    )
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault()
    if (!message.trim()) return

    setSubmitting(true)
    setError(null)

    try {
      const baseUrl = import.meta.env.VITE_API_BASE_URL ?? '/api'
      const res = await fetch(`${baseUrl}/v1/account/appeal`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: message.trim() }),
      })

      if (!res.ok) {
        const data = (await res.json()) as { error?: { message?: string } }
        throw new Error(data.error?.message ?? 'Failed to submit appeal')
      }

      setSubmitted(true)
      await refetchAccount()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="rounded-xl border border-danger/30 bg-red-50/50 p-6">
      <h2 className="text-lg font-semibold text-text-primary">Submit an Appeal</h2>
      <p className="mt-2 text-sm text-text-secondary">
        Your account is currently {level === 2 ? 'suspended' : 'under review'} because:{' '}
        <strong>{account.pause_reason}</strong>
      </p>
      <p className="mt-1 text-sm text-text-secondary">
        If you believe this is an error, explain the situation below and our team will review it.
      </p>

      <form onSubmit={handleSubmit} className="mt-4">
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Explain what happened and why the suspension should be lifted..."
          maxLength={2000}
          rows={5}
          className="w-full rounded-lg border border-surface-3 bg-surface-0 p-3 text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-text-tertiary">{message.length}/2000</span>
          {error && <span className="text-xs text-danger">{error}</span>}
          <button
            type="submit"
            disabled={submitting || !message.trim()}
            className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50"
          >
            {submitting ? 'Submitting...' : 'Submit Appeal'}
          </button>
        </div>
      </form>
    </div>
  )
}
